/**
 * Sprint S-1 · #23 · SEO head manager para SPAs.
 *
 * Hook que inyecta title + meta + JSON-LD + canonical + hreflang en
 * document.head a partir del payload de /api/seo/meta/{slug}. No usa
 * react-helmet para evitar dependencias extra — manipula el DOM directo.
 *
 * Googlebot ejecuta JS desde 2019 y ve estas tags (verificado con
 * URL Inspection Tool). Para mejor crawl-budget se sirve el sitemap.
 */
import { useEffect } from "react";
import { api } from "./api";

const MANAGED_ATTR = "data-ag-seo";

function setMetaByName(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"][${MANAGED_ATTR}="1"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    el.setAttribute(MANAGED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProperty(prop, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${prop}"][${MANAGED_ATTR}="1"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    el.setAttribute(MANAGED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, attrs = {}) {
  if (!href) return;
  const selector = attrs.hreflang
    ? `link[rel="${rel}"][hreflang="${attrs.hreflang}"][${MANAGED_ATTR}="1"]`
    : `link[rel="${rel}"][${MANAGED_ATTR}="1"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MANAGED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function setJsonLd(id, data) {
  const selector = `script[type="application/ld+json"][data-ag-seo-id="${id}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-ag-seo-id", id);
    el.setAttribute(MANAGED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function clearManaged() {
  document.head
    .querySelectorAll(`[${MANAGED_ATTR}="1"]`)
    .forEach((el) => el.remove());
}

/**
 * Aplica la metadata SEO para una ficha pública.
 * @param {Object} meta — payload de /api/seo/meta/{slug}
 * @param {string} basePath — '/reservar' o '/centro'
 */
export function applySeoMeta(meta, basePath = "/reservar") {
  if (!meta || !meta.found) return;
  const origin = window.location.origin;
  // Title
  if (meta.title) document.title = meta.title;
  // Meta description / keywords / robots
  setMetaByName("description", meta.description);
  setMetaByName("keywords", meta.keywords);
  setMetaByName("robots", "index, follow, max-image-preview:large");
  // Open Graph — og:url SIEMPRE apunta a la URL canónica (/centro/{slug})
  const og = meta.og || {};
  const slugBase = meta.slug || "";
  const ogUrl = og.url || `${origin}/centro/${slugBase}`;
  setMetaByProperty("og:title", og.title || meta.title);
  setMetaByProperty("og:description", og.description || meta.description);
  setMetaByProperty("og:image", og.image);
  setMetaByProperty("og:url", ogUrl);
  setMetaByProperty("og:type", og.type || "website");
  setMetaByProperty("og:site_name", og.site_name || "AutoGlow Ecosystem");
  // Twitter card
  setMetaByName("twitter:card", "summary_large_image");
  setMetaByName("twitter:title", og.title || meta.title);
  setMetaByName("twitter:description", og.description || meta.description);
  setMetaByName("twitter:image", og.image);
  // Canonical (auditoría SEO 06/2026): /centro/{slug} es SIEMPRE la URL SEO
  // principal. /reservar/{slug} canoniza hacia /centro/{slug} para evitar
  // contenido duplicado — Google consolida señales en la landing del centro.
  const slug = meta.slug || "";
  const canonical = meta.canonical || `${origin}/centro/${slug}`;
  setLink("canonical", canonical);
  // Hreflang — la SPA detecta idioma pero la URL es la misma; usamos x-default + es.
  // En el futuro podemos crear /es/, /en/ paths si se quiere SEO por idioma.
  setLink("alternate", canonical, { hreflang: "es" });
  setLink("alternate", canonical, { hreflang: "x-default" });
  // JSON-LD AutoRepair / LocalBusiness + BreadcrumbList
  if (meta.schema_org) setJsonLd("org", meta.schema_org);
  if (meta.breadcrumb_schema) setJsonLd("breadcrumb", meta.breadcrumb_schema);
}

/**
 * Hook que carga la metadata desde el backend y la aplica.
 * Limpia al desmontar para que la siguiente página parta de un head limpio.
 */
export function useOrgSeo(slug) {
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    api.get(`/seo/meta/${slug}`)
      .then(({ data }) => {
        if (!cancelled && data?.found) applySeoMeta({ ...data, slug });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      // Restaurar título por defecto
      document.title = "AutoGlow Ecosystem · Marketplace + SaaS para profesionales del motor";
      clearManaged();
    };
  }, [slug]);
}

/**
 * Aplica metadata simple para páginas sin org (ej: /marketplace, /ciudad/:city).
 * `jsonLd` admite un objeto o un array de objetos schema.org (memoizar en el caller).
 */
export function useStaticSeo({ title, description, path, jsonLd }) {
  useEffect(() => {
    const origin = window.location.origin;
    if (title) document.title = title;
    setMetaByName("description", description);
    setMetaByName("robots", "index, follow");
    setLink("canonical", `${origin}${path || window.location.pathname}`);
    if (jsonLd) {
      const list = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      list.forEach((d, i) => setJsonLd(`static-${i}`, d));
    }
    return () => {
      document.title = "AutoGlow Ecosystem · Marketplace + SaaS para profesionales del motor";
      clearManaged();
    };
  }, [title, description, path, jsonLd]);
}

/**
 * Slugifica nombres de ciudad para URLs SEO (/ciudad/:city).
 * Idéntico a `slugify_city` en backend/services/public_orgs.py.
 * 'Lleida' → 'lleida' · 'A Coruña' → 'a-coruna'
 */
export function slugifyCity(name) {
  return (name || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
